import { ReactReduxContext } from 'react-redux';
import { refreshUserInfoRequest } from '../../actions';
/* eslint-disable react/prop-types */
import React, { useContext, useEffect, useRef, useState } from 'react';
import Select, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { sendRequest } from '../../utils/dateHelpers';

export const flattenOptions = options =>
  (options || []).flatMap(item => item.options || [item]);
export function groupTagOptions(options, groups, catalog = []) {
  const tagById = new Map(catalog.map(tag => [tag._id, tag]));
  const buckets = new Map(
    (groups || []).map(group => [
      group._id,
      { ...group, label: group.name, options: [] }
    ])
  );
  const fallback = { label: 'General', options: [] };
  flattenOptions(options).forEach(option => {
    const group = buckets.get(
      option.group_uid || tagById.get(option.value)?.group_uid
    );
    (group || fallback).options.push(option);
  });
  return [...buckets.values(), fallback].filter(group => group.options.length);
}
export function toggleGroupSelection(selected, options) {
  const ids = new Set(options.map(option => option.value));
  const selection = selected || [];
  const all = options.every(option =>
    selection.some(item => item.value === option.value)
  );
  return all
    ? selection.filter(item => !ids.has(item.value))
    : [...selection.filter(item => !ids.has(item.value)), ...options];
}
const Option = props => (
  <components.Option {...props}>
    <span className="flex items-center gap-2.5">
      {props.isMulti && (
        <input
          type="checkbox"
          checked={props.isSelected}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          className="h-4 w-4 shrink-0 rounded accent-sky-600"
        />
      )}
      {props.label}
    </span>
  </components.Option>
);
const GroupHeading = props => {
  const { selectProps, data } = props;
  const selected = Array.isArray(selectProps.value) ? selectProps.value : [];
  const count = data.options.filter(option =>
    selected.some(item => item.value === option.value)
  ).length;
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current)
      ref.current.indeterminate = count > 0 && count < data.options.length;
  }, [count, data.options.length]);
  const headingContent = (
    <>
      {selectProps.allowGroupSelection && selectProps.isMulti && (
        <input
          ref={ref}
          type="checkbox"
          checked={count === data.options.length}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          className="h-4 w-4 shrink-0 rounded accent-sky-600"
        />
      )}
      <svg
        aria-hidden="true"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className="shrink-0 text-slate-500 dark:text-slate-400"
      >
        <path d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      </svg>
      <span className="min-w-0 flex-1 font-semibold">{data.label}</span>
      <span className="shrink-0 rounded-md bg-white px-1.5 py-0.5 text-xs font-medium tabular-nums text-slate-500 dark:bg-slate-700 dark:text-slate-300">
        {count ? `${count} / ${data.options.length}` : data.options.length}
      </span>
    </>
  );
  const headingClassName =
    'flex w-full items-center gap-2 rounded-lg bg-slate-100 px-3 py-2.5 text-left text-sm normal-case tracking-normal text-slate-800 dark:bg-slate-800 dark:text-slate-100';
  return (
    <components.GroupHeading {...props}>
      {selectProps.allowGroupSelection && selectProps.isMulti ? (
        <button
          type="button"
          className={`${headingClassName} transition-colors hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-500 dark:hover:bg-slate-700`}
          aria-label={`Select group ${data.label}`}
          aria-pressed={
            count === data.options.length ? true : count ? 'mixed' : false
          }
          onMouseDown={event => event.preventDefault()}
          onClick={() =>
            selectProps.onChange(toggleGroupSelection(selected, data.options))
          }
        >
          {headingContent}
        </button>
      ) : (
        <div className={headingClassName}>{headingContent}</div>
      )}
    </components.GroupHeading>
  );
};
const styles = {
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  menu: base => ({ ...base, minWidth: 260 }),
  group: base => ({ ...base, paddingTop: 6, paddingBottom: 6 }),
  groupHeading: base => ({
    ...base,
    padding: '0 8px',
    marginBottom: 4,
    textTransform: 'none'
  }),
  control: base => ({ ...base, backgroundColor: 'var(--tag-select-bg, white)' })
};

export default function TagSelect({
  options = [],
  groups: suppliedGroups,
  value,
  selectedValue,
  onChange,
  allowGroupSelection = false,
  allowCreate = false,
  isMulti = true,
  label,
  widthClassName = 'min-w-40',
  ...props
}) {
  const { store } = useContext(ReactReduxContext) || {};
  const [catalog, setCatalog] = useState([]);
  const [groups, setGroups] = useState(suppliedGroups || []);
  const [newTag, setNewTag] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef(null);
  const previousFocus = useRef(null);
  useEffect(() => {
    if (!newTag) return;
    previousFocus.current = document.activeElement;
    dialogRef.current?.querySelector('input')?.focus();
    return () => previousFocus.current?.focus();
  }, [Boolean(newTag)]);
  const trapDialogFocus = event => {
    if (event.key === 'Escape' && !busy) {
      event.stopPropagation();
      setNewTag(null);
    }
    if (event.key !== 'Tab') return;
    const controls = [
      ...dialogRef.current.querySelectorAll(
        'button:not(:disabled), input:not(:disabled), select:not(:disabled)'
      )
    ];
    const first = controls[0],
      last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };
  const selected = value ?? selectedValue ?? (isMulti ? [] : null);
  useEffect(() => {
    let active = true;
    if (suppliedGroups?.length) {
      setGroups(suppliedGroups);
      return;
    }
    sendRequest('/tags/get', 'GET')
      .then(result => {
        if (active) {
          setGroups(result.groups || []);
          setCatalog(result.data || []);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [suppliedGroups]);
  const createdOptions = catalog
    .filter(
      tag => !flattenOptions(options).some(option => option.value === tag._id)
    )
    .map(tag => ({
      value: tag._id,
      label: tag.tagName,
      group_uid: tag.group_uid
    }));
  const selectableOptions = [
    ...flattenOptions(options),
    ...(allowCreate ? createdOptions : [])
  ];
  const grouped = groupTagOptions(selectableOptions, groups, catalog);
  const Component = allowCreate ? CreatableSelect : Select;
  const allOptions = flattenOptions(grouped);
  const allSelected =
    allOptions.length > 0 &&
    allOptions.every(option =>
      (Array.isArray(selected) ? selected : []).some(
        item => item.value === option.value
      )
    );
  const handleChange = next => {
    if (Array.isArray(next) && next.some(item => item.value === '*'))
      onChange(allSelected ? [] : allOptions);
    else onChange(next || (isMulti ? [] : null));
  };
  return (
    <div className={`select-container ${widthClassName}`}>
      {label && (
        <label
          className="select-label dark:bg-meta-4 dark:text-white"
          htmlFor={props.inputId}
        >
          {label}
        </label>
      )}
      <Component
        classNamePrefix="my-react-select"
        className="my-react-select-container"
        {...props}
        isMulti={isMulti}
        options={
          allowGroupSelection && isMulti
            ? [
                { value: '*', label: allSelected ? 'Clear all' : 'Select all' },
                ...grouped
              ]
            : grouped
        }
        value={selected}
        onChange={handleChange}
        allowGroupSelection={allowGroupSelection}
        hideSelectedOptions={false}
        closeMenuOnSelect={!isMulti}
        components={{ Option, GroupHeading, ...props.components }}
        classNames={{
          menu: () => 'tag-groups-menu',
          group: () => 'tag-select-group',
          option: () => 'tag-select-option',
          ...props.classNames
        }}
        styles={{ ...styles, ...props.styles }}
        filterOption={(candidate, input) =>
          candidate.label.toLowerCase().includes(input.toLowerCase()) ||
          groups.some(
            group =>
              group._id ===
                (candidate.data.group_uid ||
                  catalog.find(tag => tag._id === candidate.value)
                    ?.group_uid) &&
              group.name.toLowerCase().includes(input.toLowerCase())
          )
        }
        onCreateOption={name => {
          setNewTag({ name, groupUid: '' });
          setError('');
        }}
      />
      {newTag && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4"
          onKeyDown={trapDialogFocus}
        >
          <form
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Create tag"
            className="w-full max-w-sm space-y-4 rounded-xl bg-white p-6 text-slate-900 shadow-xl dark:bg-slate-800 dark:text-white"
            onSubmit={async event => {
              event.preventDefault();
              setBusy(true);
              setError('');
              try {
                const result = await sendRequest('/tags/add', 'POST', {
                  tagName: newTag.name,
                  groupUid: newTag.groupUid
                });
                const tag = result.data;
                setCatalog(previous => [...previous, tag]);
                const option = {
                  value: tag._id,
                  label: tag.tagName,
                  group_uid: tag.group_uid
                };
                onChange(isMulti ? [...(selected || []), option] : option);
                setNewTag(null);
                store?.dispatch(refreshUserInfoRequest());
              } catch (err) {
                setError(
                  err?.errorData?.message ||
                    err?.message ||
                    'Could not create tag.'
                );
              } finally {
                setBusy(false);
              }
            }}
          >
            <h3 className="text-lg font-semibold">Create tag</h3>
            <label className="block text-sm">
              Tag name
              <input
                autoFocus
                required
                disabled={busy}
                className="mt-1 w-full rounded border border-slate-300 bg-transparent px-3 py-2"
                value={newTag.name}
                onChange={event =>
                  setNewTag({ ...newTag, name: event.target.value })
                }
              />
            </label>
            <label className="block text-sm">
              Group
              <select
                required
                disabled={busy}
                className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2 dark:bg-slate-800"
                value={newTag.groupUid}
                onChange={event =>
                  setNewTag({ ...newTag, groupUid: event.target.value })
                }
              >
                <option value="">Select a group</option>
                {groups.map(group => (
                  <option key={group._id} value={group._id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </label>
            {error && (
              <p role="alert" className="text-sm text-red-600">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => setNewTag(null)}
                className="rounded px-3 py-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy || !newTag.groupUid}
                className="rounded bg-primary px-3 py-2 text-white disabled:opacity-50"
              >
                {busy ? 'Creating…' : 'Create & assign'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
